import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { ProgramCard, ProgramDetails, ProgramIntro, ProgramRow } from "@/components/services/ProgramCard";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { AICitations } from "@/components/AICitations";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { RelatedReading } from "@/components/RelatedReading";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { faqSchema } from "@/lib/schema";

const FAQS = [
  { q: "What are AEO services?", a: "AEO services are the hands-on work that gets a brand quoted inside AI answers: auditing how ChatGPT, Perplexity, Claude and Gemini describe you today, fixing entity and schema signals, rewriting key pages into short quotable answers, and tracking citations month to month." },
  { q: "What is Answer Engine Optimization (AEO)?", a: "AEO is the practice of optimizing content, schema and entity signals so AI answer engines like ChatGPT, Perplexity, Claude and Gemini quote and cite your brand directly inside their answers." },
  { q: "How is AEO different from SEO?", a: "SEO ranks pages on the blue links. AEO earns citations inside the AI-synthesized answer above (or instead of) those links. AEO leans more on entity associations, structured data, and short, quotable answers." },
  { q: "How do I get cited by ChatGPT or Perplexity?", a: "Build clear entity associations, publish unique data and primary research, structure answers as 40–60 word quotable paragraphs, use FAQ + Article schema, and earn mentions on sources those models trust." },
  { q: "How do I choose an AEO service provider?", a: "Ask to see before-and-after AI answers for a real client, how citations are tracked, and what is excluded. Avoid anyone guaranteeing placement in ChatGPT — no one controls that. A good provider also fixes your classic SEO, because AI answers draw on pages search engines already trust." },
  { q: "Do you offer AEO for SaaS and B2B companies?", a: "Yes. SaaS and B2B brands are a strong fit because buyers ask AI assistants to compare tools. The work focuses on comparison, alternatives and use-case pages plus consistent entity data across review sites." },
  { q: "How long until my site starts getting AI citations?", a: "There is no fixed timeline and no one can guarantee a citation. AI answers change often, so the work starts with a recorded baseline of how assistants answer your key questions, then re-checks the same prompts monthly after entity, schema and content fixes ship." },
];

const DELIVERABLES = [
  "Entity & brand knowledge graph build-out",
  "Citation-friendly content rewrites (40–60 word answer blocks)",
  "FAQPage, Article & Organization schema with sameAs",
  "AI visibility tracking across ChatGPT, Perplexity, Claude, Gemini",
  "llms.txt + AI crawler access policy",
  "Monthly citation & share-of-voice report",
];

const OUTCOMES = ["Cited in ChatGPT & Perplexity", "Branded mentions in LLM outputs", "Defensible share of AI search"];

export const Route = createFileRoute("/services/aeo")({
  head: () => buildSeo({
    title: "AEO Services — Answer Engine Optimization for ChatGPT & Perplexity | Freelancer Tamal",
    description: "AEO services that get your brand cited in ChatGPT, Perplexity, Claude and Gemini: AI visibility audit, entity and schema fixes, quotable answer rewrites, and monthly citation tracking. See prices.",
    path: "/services/aeo",
    alternates: { bn: "/bn/services/aeo" },
    ogEyebrow: "AEO",
    keywords: "AEO services, AEO expert Bangladesh, answer engine optimization, ChatGPT SEO, Perplexity SEO, Claude citations, AI search optimization",
  }),
  component: AeoPage,
});

function AeoPage() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "AEO", path: "/services/aeo" }]} />
      <Section headingLevel={1}
        eyebrow="Answer Engine Optimization"
        title={<>Get cited by <span className="text-gradient">ChatGPT, Perplexity & Claude.</span></>}
        subtitle="AEO is how modern brands win share-of-voice inside AI answers — where more and more questions are answered before anyone clicks a link."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "AEO earns your brand citations inside ChatGPT, Perplexity, Claude and Gemini — not just rankings on Google.",
              "It works by stacking entity authority, citation-worthy answer blocks, and the right schema (FAQPage, Article, Organization with sameAs).",
              "Brands ignoring AEO keep ranking on Google but lose share-of-voice in AI search every month.",
            ]}
          />
          <p className="mt-6 text-muted-foreground">
            <strong className="text-foreground">What are AEO services?</strong> Hands-on work that gets your brand quoted inside AI answers — audit, entity and schema fixes, answer-first rewrites, and citation tracking. See the AEO Sprint on the{" "}
            <Link to="/pricing" className="text-primary underline underline-offset-4">pricing page</Link>, or pair it with{" "}
            <Link to="/services/geo" className="text-primary underline underline-offset-4">GEO services</Link> for Google AI Overviews.
          </p>
          <ReviewedBy reviewedOn="2026-06-01" credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh" />
        </div>
      </Section>

      <ProgramCard>
        <ProgramIntro label="Service" title="AEO program" tagline="A 90-day Answer Engine Optimization sprint that turns your site into a source AI assistants quote.">
          <div className="mt-8">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
              Start an AEO engagement <ArrowUpRight size={16} />
            </Link>
          </div>
        </ProgramIntro>
        <ProgramDetails deliverables={DELIVERABLES} outcomes={OUTCOMES}>
          <ProgramRow heading="Live proof — recent AI citations">
            <AICitations />
          </ProgramRow>
          <ProgramRow heading="People also ask">
            <FaqAccordion items={FAQS} idPrefix="faq-aeo" withSchema={false} />
          </ProgramRow>
        </ProgramDetails>
      </ProgramCard>

      <Section title={<>Pair AEO with <span className="text-gradient">GEO</span> for full AI-search coverage.</>}>
        <div className="flex flex-wrap gap-3">
          <Link to="/services/geo" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">See the GEO program <ArrowUpRight size={16} /></Link>
          <Link to="/services" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">All services <ArrowUpRight size={16} /></Link>
        </div>
        <AiAnswersCallout
          context="AEO & LLM citations"
          questionSlugs={[
            "get-cited-by-chatgpt",
            "how-chatgpt-picks-citations",
            "aeo-time-to-results",
            "faqpage-schema-worth-it",
          ]}
        />
      </Section>

      <RelatedReading
        eyebrow="Deep-dive guides"
        title={<>Read before you <span className="text-gradient">hire anyone for AEO.</span></>}
        hrefs={[
          "/blog/what-is-aeo-how-to-get-cited-by-chatgpt-2026",
          "/blog/schema-markup-for-aeo-2026",
          "/blog/entity-seo-signals-for-aeo",
          "/blog/common-aeo-mistakes-to-avoid",
          "/blog/cite-framework-aeo",
          "/blog/in-house-vs-agency-vs-fractional-aeo",
        ]}
      />

      <JsonLd data={{
        "@context": "https://schema.org", "@type": "Service",
        name: "Answer Engine Optimization (AEO)",
        serviceType: "Answer Engine Optimization",
        description: "Get cited by ChatGPT, Perplexity, Claude and Gemini through entity SEO, schema markup, and citation-worthy content.",
        areaServed: ["Bangladesh", "Worldwide"],
        provider: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/about`, jobTitle: "SEO & AEO Expert", sameAs: Object.values(SITE.social) },
      }} />
      <JsonLd data={faqSchema(FAQS)} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "AEO", path: "/services/aeo" }])} />
    </>
  );
}